import { gql } from "lib/apolloClient";
import { plannerFieldFragment } from "UnitPlans/modules/UnitPlanFragments";

export const assessmentFragment = gql`
  fragment assessmentItem on Assessment {
    id
    type
    title {
      id
      value
    }
    academicTerm {
      id
      label
      lockingStatus
    }
    assessmentType {
      id
      value
    }
    taskType {
      id
      type
      label
      color
      displaySequence
      metaData
    }
    image {
      id
      value
    }
    category {
      id
      label
    }
    globalCategories {
      id
      name
    }
    lockingInfo {
      isLocked
    }
    isSyncedFromBluePrintCourse(filters: $syncedBluePrintItemFilters)
    isSyncedWithAllTeacherCourseV2(filters: $syncedBluePrintItemFilters) {
      isConflictExists
      status
      courseSyncStatus {
        course {
          id
          title
        }
        status
      }
    }
    assignments(filters: $assessmentAssignmentsFilters) {
      edges {
        id
        state {
          state
        }
        course {
          id
        }
        assignedStudents(studentIds: $studentIds)
          @skip(if: $skipAssignedStudents) {
          id
          status
          submission {
            id
            statusV2
          }
          isEvaluationApplicable
          isEvaluated
        }
      }
    }
  }
`;

export const attachmentFragment = gql`
  fragment attachmentItem on Attachment {
    id
    attachmentType: type
    attachmentTitle: title
    name
    mimeType
    url
    signedUrl
    thumbUrl
    __typename
  }
`;

export const unitPlanFragment = gql`
  fragment unitPlanItem on UnitPlan {
    id
    templateId
    title {
      id
      value
    }
    unitType {
      id
      value
    }
    startDate
    endDate
    owner {
      id
    }
    isSharedWithStudents
    isSharedWithFamilies
    enabledSections {
      userType
      sections
    }
    lockingInfo {
      isLocked
    }
    isSyncedFromBluePrintCourse(filters: $syncedBluePrintItemFilters)
    isSyncedWithAllTeacherCourseV2(filters: $syncedBluePrintItemFilters) {
      isConflictExists
      status
      courseSyncStatus {
        course {
          id
          title
        }
        status
      }
    }
    defaultStaffAccess
    state
    image {
      id
      value
      resolvedMinimalTree {
        ... on ResolvedFieldVideoAttachment {
          id
          type
          attachment {
            id
            url
            thumbUrl
          }
        }
      }
    }
  }
`;

export const courseFlowResourceFragment = {
  basicDetails: gql`
    fragment courseFlowResource on LearningCourseFlow {
      id
      label
      depth
      resourceType: itemType
      isRepeated
      repeatedResources
      state(filters: $stateFilters)
      parent {
        id
      }
      item {
        id
        ... on Assessment {
          type
          academicTerm {
            id
            label
            lockingStatus
          }
          assessmentType {
            id
            value
          }
          lockingInfo {
            isLocked
          }
        }
        ... on UnitPlan {
          lockingInfo {
            isLocked
          }
        }
      }
      lockingInfo {
        isLocked
      }
      isSyncedFromBluePrintCourse(filters: $syncedBluePrintItemFilters)
      isSyncedWithAllTeacherCourseV2(filters: $syncedBluePrintItemFilters) {
        isConflictExists
        status
        courseSyncStatus {
          course {
            id
            title
          }
          status
        }
      }
      childrenV2: children(filters: $filters) {
        id
      }
      childrenHierarchyInfo(filters: $filters) {
        maxDepth
        totalCount
        totalCountWithFolder
      }
      isSyncedWithAllTeacherCourse
    }
  `,
  completeDetails: gql`
    fragment courseFlowResource on LearningCourseFlow {
      id
      label
      depth
      resourceType: itemType
      isRepeated
      repeatedResources
      parent {
        id
      }
      item {
        id
        ... on Assessment {
          ...assessmentItem
        }
        ... on Attachment {
          ...attachmentItem
        }
        ... on UnitPlan {
          ...unitPlanItem
        }
      }
      state(filters: $stateFilters)
      sharedDetails {
        class {
          id
          title
        }
        students {
          id
        }
      }
      lockingInfo {
        isLocked
      }
      isSyncedFromBluePrintCourse(filters: $syncedBluePrintItemFilters)
      isSyncedWithAllTeacherCourseV2(filters: $syncedBluePrintItemFilters) {
        isConflictExists
        status
        courseSyncStatus {
          course {
            id
            title
          }
          status
        }
      }
      childrenV2: children(filters: $filters) {
        id
        label
        resourceType: itemType
        displaySequence
      }
      childrenHierarchyInfo(filters: $filters) {
        maxDepth
        totalCount
        totalCountWithFolder
      }
      isSyncedWithAllTeacherCourse
    }

    ${assessmentFragment}
    ${attachmentFragment}
    ${unitPlanFragment}
  `,
};

export const courseFlowOutlineFragment = gql`
  fragment courseFlowOutlineItem on LearningCourseFlow {
    id
    label
    depth
    childrenV2: children(filters: $filters) {
      id
    }
    parent {
      id
    }
    resourceType: itemType
    item {
      ... on Assessment {
        title {
          value
        }
      }
    }
  }
`;

export const learningCourseItemFragment = gql`
  fragment learningCourseItem on LearningCourse {
    id
    title
    type
    isArchived
    grades(filters: $gradeFilters) {
      id
      name
    }
    subjects {
      id
      name
    }
    academicYears {
      id
      startDate
      endDate
    }
    courseFlowItemCounts {
      unitPlanCount
    }
  }
`;
export const unitPlanDetailsFragment = {
  unitPlanDetails: gql`
    fragment unitPlanItem on UnitPlan {
      id
      unitType {
        ...unitPlanFieldItemWithResponseFilter
      }
      allFields {
        ...unitPlanFieldItemWithResponseFilter
      }
      isImported
      enabledSections {
        userType
        sections
      }
      isSharedWithStudents
      isSharedWithFamilies
      shareWithUserType
    }
    ${plannerFieldFragment.unitPlanFieldWithResponseFilter}
  `,
};
